export type PrintComCompatibilityIssue = {
  code: string;
  message: string;
  keys: string[];
};

const POSTER_SKUS = new Set(['posters', 'photo-poster', 'photo_poster', 'foto-op-poster', 'foto_op_poster', 'poster']);

export const POSTER_MATERIALS_WITHOUT_FINISH = new Set([
  '95gr_neon_papier_geel',
  '95gr_neon_papier_groen',
  '95gr_neon_papier_magenta',
  '95gr_neon_papier_oranje',
  '115gr_blueback',
  '120gr_biotop',
  '120gr_recystar',
  '120gr_recystar_nature',
  '135gr_gesatineerd_mc',
  '135gr_silk_mc',
  '150gr_citylight',
  '160gr_ijm814_uncoated',
  '170gr_gesatineerd_mc',
  '170gr_silk_mc',
  '170gr_synaps',
  '230gr_premium_matt',
  '280gr_backlit_polyester',
  '510gr_pvc',
]);

export const NO_POSTER_FINISH_VALUES = new Set([
  'geen',
  'none',
  'no',
  'no_laminate',
  'no_lamination',
  'without_lamination',
]);

export function isPrintComPosterSku(value: unknown) {
  return POSTER_SKUS.has(String(value || '').toLowerCase());
}

export function isNoPosterFinishValue(value: unknown) {
  return NO_POSTER_FINISH_VALUES.has(String(value ?? '').toLowerCase());
}

export function isPosterMaterialWithoutFinish(value: unknown) {
  return POSTER_MATERIALS_WITHOUT_FINISH.has(String(value ?? ''));
}

const positive = (value: unknown) => Number(value) > 0;

/**
 * `size=custom` zonder ingevulde breedte en hoogte. Print.com antwoordt dan
 * "missing required size key width"; bij boekenleggers en sleeves ging zo'n
 * aanvraag de deur uit zodra de klant "eigen formaat" koos (sweep 19-10-2026).
 * Herkent onze schermvelden (`size_width`/`size_height`, cm), de leveranciers-
 * velden (`width`/`height`, mm) en de korte vorm `custom:B:H`.
 */
export function hasIncompleteCustomSize(options?: Record<string, unknown> | null): boolean {
  const size = options?.size;
  if (typeof size !== 'string') return false;
  if (size.startsWith('custom:')) {
    const [, width, height] = size.split(':');
    return !(positive(width) && positive(height));
  }
  if (size !== 'custom') return false;
  return !(
    (positive(options?.size_width) && positive(options?.size_height)) ||
    (positive(options?.width) && positive(options?.height))
  );
}

export const CUSTOM_SIZE_INCOMPLETE = 'CUSTOM_SIZE_INCOMPLETE';

export function getPrintComOptionCompatibilityIssue(args: {
  sku?: unknown;
  productSlug?: unknown;
  providerProductId?: unknown;
  options?: Record<string, unknown> | null;
}): PrintComCompatibilityIssue | null {
  if (hasIncompleteCustomSize(args.options)) {
    return {
      code: CUSTOM_SIZE_INCOMPLETE,
      message: 'Vul de breedte en hoogte van het eigen formaat in.',
      keys: ['size_width', 'size_height'],
    };
  }

  const productKey = args.providerProductId ?? args.sku ?? args.productSlug;
  if (!isPrintComPosterSku(productKey)) return null;

  const options = args.options || {};
  const material = options.material;
  const finish = options.finish;

  if (
    material !== undefined &&
    finish !== undefined &&
    isPosterMaterialWithoutFinish(material) &&
    !isNoPosterFinishValue(finish)
  ) {
    return {
      code: 'INVALID_PRINTCOM_OPTION_COMBINATION',
      message: 'Deze materiaal-afwerking combinatie is niet beschikbaar bij Print.com.',
      keys: ['material', 'finish'],
    };
  }

  return null;
}
